import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { GuiaProatividadeService } from '../../services/tour-mode/guia-proatividade.service';

/**
 * D70/D71/D76 — balão único do guia no orbe do assistente: renderiza o `teaser` já ARBITRADO
 * por `GuiaProatividadeService` (erro > convite > primeira-visita > conclusão — D76), nunca
 * decide nada sozinho. Antes da Task 5 este componente montava o convite (D70) direto de
 * `GuiaOneService.oferta()`; agora só lê a forma já pronta (`TeaserGuia`).
 */
@Component({
  selector: 'guia-teaser',
  standalone: true,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!oculto() && teaser(); as t) {
      <div class="guia-teaser" role="status">
        <mat-icon class="guia-teaser-icone" aria-hidden="true">{{ t.icone }}</mat-icon>
        <span class="guia-teaser-titulo">{{ t.titulo }}</span>
        @if (t.acao; as a) {
          <button type="button" class="guia-teaser-acao" data-test="teaser-acao" (click)="a.executar()">{{ a.rotulo }}</button>
        }
        <button type="button" class="guia-teaser-fechar" data-test="teaser-fechar" (click)="t.fechar()" aria-label="Fechar aviso">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: `
    :host { display: contents; }
    /* 2026-10-13 (decisão do usuário): UMA linha, pequena e harmônica com o .ai-teaser vizinho — ícone,
     * frase curta, um botão de aceite e o X. Mesma âncora de sempre: à frente da aba do assistente. */
    .guia-teaser {
      position: fixed;
      right: 48px;
      bottom: calc(var(--one-footer-height, 56px) - 16px);
      height: 36px;
      max-height: 40px;
      box-sizing: border-box;
      z-index: 9000;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      padding: 0 6px 0 12px;
      max-width: min(520px, calc(100vw - 72px));
      background: var(--one-glass-bg);
      backdrop-filter: var(--one-glass-blur);
      -webkit-backdrop-filter: var(--one-glass-blur);
      color: var(--text-100);
      border: 1px solid var(--gray-400);
      border-radius: 999px;
      box-shadow: var(--one-elevation);
      font-family: "Nunito Sans", sans-serif;
      animation: guia-teaser-entra 180ms ease both;
    }
    @keyframes guia-teaser-entra {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: none; }
    }
    .guia-teaser-icone {
      color: var(--ai-accent, var(--main-200));
      font-size: 18px; width: 18px; height: 18px; flex-shrink: 0;
    }
    .guia-teaser-titulo {
      min-width: 0; flex: 1 1 auto;
      font-size: 12.5px; font-weight: 600; line-height: 1.2; color: var(--text-100);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .guia-teaser-acao {
      flex-shrink: 0;
      border: none; border-radius: 999px; padding: 5px 11px; cursor: pointer;
      font-family: inherit; font-size: 11.5px; font-weight: 700;
      background: var(--main-200); color: var(--main-text-100);
      transition: background 0.15s ease;
    }
    .guia-teaser-acao:hover { background: var(--main-100); }
    .guia-teaser-fechar {
      flex-shrink: 0;
      width: 22px; height: 22px; padding: 0; border: none; border-radius: 50%;
      background: none; color: var(--text-300); cursor: pointer;
      display: grid; place-items: center;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .guia-teaser-fechar:hover { background: var(--gray-300); color: var(--text-100); }
    .guia-teaser-fechar mat-icon { font-size: 15px; width: 15px; height: 15px; }
  `,
})
export class GuiaTeaserComponent {
  private readonly proatividade = inject(GuiaProatividadeService);

  /** Some quando outro balão do orbe (o alerta contextual, prioridade máxima — D76) já está
   *  visível, OU quando o chat está aberto: o overlay liga isto a `mostrarBalao() || svc.aberto()`. */
  readonly oculto = input<boolean>(false);

  /** D76 — já arbitrado por `GuiaProatividadeService`; este componente só renderiza. */
  readonly teaser = computed(() => this.proatividade.teaser());
}
